import { gsap } from 'gsap';
import { containerNames, SPIN_BUTTON_ARROWS_LOOP_ANIMATION_DURATION } from '../../constants';
import { PixiBase } from '../../core/pixi/PixiBase';
import { Spine } from '../../services/elementTypeService/Spine';
import { isMobile } from '../../helpers/isMobile';
import { gameLiveConnection, soundService } from '../../services';
import { jackpotIcons, jackpotIconsAnimationPosition } from '../../enums/jackpotIcons';
import { gameStore } from '../../store/gameStore';
import { winBarStore } from '../../store/winBarStore';
import { applicationStore } from '../../store/applicationStore';
import {
  clearJackPotTimeout,
  createJackpotAmountSymbols,
  createJackpotSymbolContainer,
  updateExpiresCount,
} from '../../helpers/jackpotHelpers';
// eslint-disable-next-line import/named
import { CountUp } from '../../services/elementTypeService/gameWin/gameWinPopups';
import { spinBarStore } from '../../store/spinBarStore';
import { gameMainDrawer } from './gameMainDrawer';
import { SoundTypes } from '../../services/baseSoundService/soundService';
import { BitmapText } from '../../services/elementTypeService/BitmapText';
import { JACK_POTS_AMOUNT_KEY } from '../../core/constants';
import { parseNumberPrecisionByDoteCount } from '../../helpers/parseNumberByPrecision';
import { userInfoStore } from '../../store/userInfoStore';
import { isoStandard } from '../../helpers/isoStandard';
import { exchangeAmount } from '../../helpers/exchangeAmountByRate';

export class JackpotDrawer {
  constructor() {
    this.animations = {};
    this.JackpotContainer = PixiBase.createContainer(containerNames.JackpotContainer);
    this.jackpotSceneAmountBoardContainers = PixiBase.createContainer(
      'jackpotSceneAmountBoardContainers'
    );
    this.countup = null;
    this.jackpotPopupEndTime = null;
    this.closeTimeJackpotScane = null;
    this.jackpotWinPopupEndTime = null;
    this.spine = null;
    this.time = 3000;
    this.jackpotAmounts = [];
  }

  setupActiveColumn() {
    this.activeColumn = new Spine({ key: 'jackpotActiveColumn' });
    const { x, y } = this.config.JackpotContainer.activeColumnConfig;
    PixiBase.setPosition(this.activeColumn, { x: x, y: y });
    // this.activeColumn.scale.set(0.85);
    this.JackpotContainer.addChild(this.activeColumn);
  }

  setupJackpotStartPopup() {
    this.jackpotTextureContainer = PixiBase.createContainer('jackpotTextureContainer');
    this.jackpotTextureContainer.visible = false;
    this.jackpotStartPopup = new Spine({ key: 'jackpotStartPopup' });
    this.jackpotStartPopup.visible = false;
    const { x, y } = this.config.JackpotContainer.jackpotStartPopup;
    PixiBase.setPosition(this.jackpotStartPopup, { x: x, y: y });
    this.jackpotTextureContainer.addChild(this.jackpotStartPopup);
    this.JackpotContainer.addChild(this.jackpotTextureContainer);
  }

  startPopup() {
    applicationStore.setIsShowJackpot(false);
    spinBarStore.setIsSpinStart(true);
    this.jackpotStartPopup.interactive = true;
    this.jackpotTextureContainer.visible = true;
    this.jackpotStartPopup.visible = true;
    this.jackpotStartPopup.clearListeners();
    this.jackpotStartPopup.play('start', false);
    this.jackpotStartPopup.addListener(
      'complete',
      () => {
        this.jackpotStartPopup.play('loop', true);
      },
      this,
      'start'
    );
    this.jackpotPopupEndTime = setTimeout(() => {
      this.jackpotStartPopup.play('end', false);
      soundService.stop(SoundTypes.JackpotWinPopUp);
      this.jackpotStartPopup.addListener(
        'whiteEnd',
        () => {
          spinBarStore.setIsSpinStart(true);
          gameStore.setJackpotAmountVisible(true);
          this.jackpotSceneTextureContainer.visible = true;
          this.openJackpotScene();
          updateExpiresCount();
        },
        this,
        'end'
      );
    }, this.time);
  }

  updateJackpotSceneAmount(data) {
    this.jackpotAmounts.forEach((element) => {
      const newAmount = exchangeAmount(data[element.id]);
      const isInteger = data[element.id] === Math.floor(data[element.id]);
      element.style.fontSize = isMobile()
        ? `${isoStandard(newAmount)}`.length > 14
          ? 16
          : 18
        : `${isoStandard(newAmount)}`.length > 14
        ? 22
        : 24;
      gsap.to(element, {
        textContent: newAmount || 0,
        duration: 0.8,
        ease: 'none',
        snap: { textContent: isInteger ? 1 : 1 / 10 ** userInfoStore.precision },
        stagger: {
          each: 1.0,
          onUpdate: function () {
            element.text = isoStandard(
              +parseNumberPrecisionByDoteCount(
                +this.targets()[0].textContent,
                1,
                isInteger ? 0 : userInfoStore.precision
              )
            );
          },
          onComplete: function () {
            element.text = isoStandard(
              +parseNumberPrecisionByDoteCount(
                +this.targets()[0].textContent,
                1,
                isInteger ? 0 : userInfoStore.precision
              )
            );
          },
        },
      });
    });
  }

  openJackpotScene() {
    soundService.play(SoundTypes.JackpotBackground);
    this.jackpotStartPopup.interactive = false;
    this.jackptGameSceneBoard.interactive = true;
    gameMainDrawer.spinBar.hideAutoBetPopup();
    this.jackpotSceneAmountBoardContainers.children.forEach((el) => {
      if (el.id) {
        el.play('shine', true);
      }
    });
    this.jackptGameSceneBoard.play('idle', true);
  }

  setupJackpotGameSceneBg() {
    this.jackpotSceneTextureContainer = PixiBase.createContainer('jackpotSceneTextureContainer');
    this.jackpotSceneTextureContainer.visible = false;
    this.jackptGameSceneBg = PixiBase.createSprite(this.spriteSheet.jackpotBg);
    this.jackptGameSceneBg.x = isMobile() ? 1.5 : -4.5;
    this.jackptGameSceneBg.y = isMobile() ? 0.5 : 0;
    this.jackptGameSceneBoard = new Spine({ key: 'jackpotGameSceneBoard' });
    const { x, y } = this.config.JackpotContainer.jackpotGameSceneBoard;
    PixiBase.setPosition(this.jackptGameSceneBoard, { x: x, y: y });
    this.jackpotSceneTextureContainer.addChild(this.jackptGameSceneBg);
    this.jackpotSceneTextureContainer.addChild(this.jackptGameSceneBoard);
    this.JackpotContainer.addChild(this.jackpotSceneTextureContainer);
  }

  setupJackpotSymbolsBg() {
    this.jackpotSymbolsBg = PixiBase.createSprite(this.spriteSheet.symbolsContainerBg);
    const { x, y } = this.config.JackpotContainer.jackpotSymbolsBg;
    PixiBase.setPosition(this.jackpotSymbolsBg, { x: x, y: y });
    this.jackpotSceneTextureContainer.addChild(this.jackpotSymbolsBg);
  }

  setupJackpotSymbolsContainer() {
    this.jackpotSceneTextureContainers = createJackpotSymbolContainer(
      'jackpotSymbolContainer',
      'jackpotIcon'
    );
    const symbolsContainer = this.jackpotSceneTextureContainers.children;
    const { x, y } = this.config.JackpotContainer.jackpotSceneTextureContainers;
    PixiBase.setPosition(this.jackpotSceneTextureContainers, { x: x, y: y });
    for (let i = 0; i <= symbolsContainer.length; i += 1) {
      if (symbolsContainer[i]?.id) {
        PixiBase.setupButtonMode(symbolsContainer[i], {
          interactive: true,
          buttonMode: true,
        });
        PixiBase.registerPointerup(symbolsContainer[i], (e) => {
          this.jackpotSceneTextureContainers.children.forEach((el) => {
            if (el.key === 'jackpotSymbolContainer') {
              el.interactive = false;
              el.buttonMode = false;
            }
          });
          gameLiveConnection.getJackpotSymbol({
            index: e.target.id,
          });
          // Math.random() > 0.38 ? 9999 :
          this.spine = e.target;
          PixiBase.setupButtonMode(this.spine, {
            interactive: false,
            buttonMode: false,
          });
        });
        PixiBase.registerPointerover(symbolsContainer[i], (e) => {
          if (!isMobile()) e.target.play('hover', false);
        });
        PixiBase.registerPointerout(symbolsContainer[i], (e) => {
          e.target.stop();
        });
        // PixiBase.registerPointerUpOutside(symbolsContainer[i], (e) => {
        //   e.target?.stop();
        // });
      }
    }
    this.jackpotSceneTextureContainer.addChild(this.jackpotSceneTextureContainers);
  }

  resetJackpotSymbolsInteractive() {
    this.jackpotSceneTextureContainers.children.forEach((el) => {
      if (el.key === 'jackpotSymbolContainer') {
        if (el.getCurrentAnimationName() !== 'open') {
          el.interactive = true;
          el.buttonMode = true;
        }
      }
    });
  }

  updateJackpotItemScene = (jackpotSceneItemResult) => {
    if (
      jackpotSceneItemResult.jackpotSceneItemInfo?.jackpotCard &&
      jackpotSceneItemResult.jackpotSceneItemInfo.index
    ) {
      const jackpotCard = jackpotSceneItemResult?.jackpotSceneItemInfo?.jackpotCard;
      const jackpotSceneItemInfos = jackpotSceneItemResult?.jackpotSceneItemInfos;
      const jackpotIcon =
        this.jackpotSceneTextureContainers.children[0].children[
          jackpotSceneItemResult.jackpotSceneItemInfo.index - 1
        ];

      const jackpotMoveIcon =
        this.jackpotSceneTextureContainers.children[
          this.jackpotSceneTextureContainers.children.length - 1
        ].children[jackpotSceneItemResult.jackpotSceneItemInfo.index - 1];

      // if (jackpotIconsAnimationPosition[jackpotCard - 1].count <= 3) {
      this.spine.clearListeners();
      this.spine.play('open', false);
      this.spine.isOpened = true;
      jackpotIcon.visible = true;
      jackpotIcon.play('win', false);
      jackpotIcon.cardType = jackpotCard;
      jackpotIcon.skeleton.setSkinByName(jackpotIcons[jackpotCard]);
      // if (jackpotIconsAnimationPosition[jackpotCard - 1].count === 3) {
      this.jackpotSceneTextureContainers.children.forEach((el) => {
        if (el.key === 'jackpotSymbolContainer') {
          el.interactive = false;
          el.buttonMode = false;
        }
      });
      if (jackpotSceneItemResult?.jackpotSceneItemInfo?.isWin) {
        soundService.play(SoundTypes.JackpotIlluminations);
        this.jackptGameSceneBoard.play(`win_${jackpotIcons[jackpotCard]}`, true);
        this.jackpotSceneTextureContainers.children.forEach((el) => {
          if (el.key === 'jackpotSymbolContainer') {
            el.interactive = false;
            el.buttonMode = false;
          }
        });
      }
      // }
      jackpotIcon.addListener(
        'complete',
        () => {
          jackpotIcon.clearListeners();
          jackpotMoveIcon.skeleton.setSkinByName(jackpotIcons[jackpotCard]);
          jackpotMoveIcon.visible = true;
          jackpotMoveIcon.play('win', false);
          jackpotMoveIcon.addListener(
            'complete',
            () => {
              jackpotMoveIcon.clearListeners();
              if (jackpotSceneItemResult?.jackpotSceneItemInfo?.isWin) {
                this.jackpotSceneTextureContainers.children[0].children.forEach((el) => {
                  // eslint-disable-next-line no-unsafe-optional-chaining
                  if (+el.cardType === +jackpotSceneItemResult?.jackpotSceneItemInfo?.jackpotCard) {
                    el.skeleton.setSkinByName(
                      jackpotIcons[jackpotSceneItemResult?.jackpotSceneItemInfo?.jackpotCard]
                    );
                    el.play('win', true);
                  }
                });
                clearJackPotTimeout();
                this.jackptGameSceneBoard.play(`win_${jackpotIcons[jackpotCard]}`, true);
                this.jackpotSceneAmountBoardContainers.children.forEach((el) => {
                  if (el.id === jackpotCard) {
                    el.play('win', true);
                  }
                });
              } else {
                this.resetJackpotSymbolsInteractive();
              }
              if (jackpotIconsAnimationPosition[jackpotCard - 1].id === jackpotCard) {
                jackpotIconsAnimationPosition[jackpotCard - 1].count += 1;
              }
              if (isMobile()) {
                jackpotMoveIcon.scale.set(
                  jackpotIconsAnimationPosition[jackpotCard - 1].positions[
                    jackpotIconsAnimationPosition[jackpotCard - 1].count - 1
                  ].scale
                );
              }
              if (jackpotIconsAnimationPosition[jackpotCard - 1].count <= 3) {
                this.jackpotMoveAnimation(
                  jackpotMoveIcon,
                  jackpotIconsAnimationPosition[jackpotCard - 1].positions[
                    jackpotIconsAnimationPosition[jackpotCard - 1].count - 1
                  ].x,
                  jackpotIconsAnimationPosition[jackpotCard - 1].positions[
                    jackpotIconsAnimationPosition[jackpotCard - 1].count - 1
                  ].y,
                  jackpotIconsAnimationPosition[jackpotCard - 1].count,
                  jackpotCard,
                  jackpotSceneItemInfos
                );
              }
            },
            this,
            'win'
          );
        },
        this,
        'open'
      );
    }
    // }
  };

  setupDimondAmountBoard() {
    const container = PixiBase.createContainer('DiamondAmountBoard');
    this.jackpotDimondAmountBoard = new Spine({ key: 'jackpotAmountBoard' });
    this.jackpotDimondAmountBoard.id = 2;
    const text = PixiBase.createText('', {
      fill: '#FFFFFF',
      fontSize: 26,
      fontFamily: 'Roboto',
      fontWeight: 600,
    });
    text.anchor.x = 0.5;
    text.anchor.y = 0.5;
    text.id = JACK_POTS_AMOUNT_KEY.Diamond;
    this.jackpotAmounts.push(text);
    const { x, y } = this.config.JackpotContainer.jackpotDimondAmountBoard;
    PixiBase.setPosition(container, { x: x, y: y });
    PixiBase.setPosition(text, { y: isMobile() ? 9 : 6 });
    this.jackpotDimondAmountBoard.play('shine', true);
    container.addChild(this.jackpotDimondAmountBoard);
    container.addChild(text);
    this.jackpotSceneAmountBoardContainers.addChild(container);
    this.jackpotSceneTextureContainer.addChild(this.jackpotSceneAmountBoardContainers);
  }

  setupHeartAmountBoard() {
    const container = PixiBase.createContainer('HeartAmountBoard');
    this.jackpotHeartAmountBoard = new Spine({ key: 'jackpotAmountBoard' });
    this.jackpotHeartAmountBoard.id = 3;
    const text = PixiBase.createText('', {
      fill: '#FFFFFF',
      fontSize: 26,
      fontFamily: 'Roboto',
      fontWeight: 600,
    });
    text.anchor.x = 0.5;
    text.anchor.y = 0.5;
    text.id = JACK_POTS_AMOUNT_KEY.Heart;
    this.jackpotAmounts.push(text);
    const { x, y } = this.config.JackpotContainer.jackpotHeartAmountBoard;
    PixiBase.setPosition(container, { x: x, y: y });
    PixiBase.setPosition(text, { y: isMobile() ? 9 : 6 });
    this.jackpotHeartAmountBoard.play('shine', true);
    container.addChild(this.jackpotHeartAmountBoard);
    container.addChild(text);
    this.jackpotSceneAmountBoardContainers.addChild(container);
    this.jackpotSceneTextureContainer.addChild(this.jackpotSceneAmountBoardContainers);
  }

  setupSpadesAmountBoard() {
    const container = PixiBase.createContainer('SpadesAmountBoard');
    this.jackpotSpadesAmountBoard = new Spine({ key: 'jackpotAmountBoard' });
    this.jackpotSpadesAmountBoard.id = 4;
    const text = PixiBase.createText('', {
      fill: '#FFFFFF',
      fontSize: 26,
      fontFamily: 'Roboto',
      fontWeight: 600,
    });
    text.anchor.x = 0.5;
    text.anchor.y = 0.5;
    text.id = JACK_POTS_AMOUNT_KEY.Spade;
    this.jackpotAmounts.push(text);
    const { x, y } = this.config.JackpotContainer.jackpotSpadesAmountBoard;
    PixiBase.setPosition(container, { x: x, y: y });
    PixiBase.setPosition(text, { y: isMobile() ? 9 : 6 });
    this.jackpotSpadesAmountBoard.play('shine', true);
    container.addChild(this.jackpotSpadesAmountBoard);
    container.addChild(text);
    this.jackpotSceneAmountBoardContainers.addChild(container);
    this.jackpotSceneTextureContainer.addChild(this.jackpotSceneAmountBoardContainers);
  }

  setupClubsAmountBoard() {
    const container = PixiBase.createContainer('ClubsAmountBoard');
    this.jackpotClubsAmountBoard = new Spine({ key: 'jackpotAmountBoard' });
    this.jackpotClubsAmountBoard.id = 1;
    const text = PixiBase.createText('', {
      fill: '#FFFFFF',
      fontSize: 26,
      fontFamily: 'Roboto',
      fontWeight: 600,
    });
    text.anchor.x = 0.5;
    text.anchor.y = 0.5;
    text.id = JACK_POTS_AMOUNT_KEY.Club;
    this.jackpotAmounts.push(text);
    const { x, y } = this.config.JackpotContainer.jackpotClubsAmountBoard;
    PixiBase.setPosition(container, { x: x, y: y });
    PixiBase.setPosition(text, { y: isMobile() ? 9 : 6 });
    this.jackpotClubsAmountBoard.play('shine', true);
    container.addChild(this.jackpotClubsAmountBoard);
    container.addChild(text);
    this.jackpotSceneAmountBoardContainers.addChild(container);
    this.jackpotSceneTextureContainer.addChild(this.jackpotSceneAmountBoardContainers);
  }

  setupHeartSymbol() {
    const heartSymbolContainer = createJackpotAmountSymbols(this.spriteSheet.heartWInBoard);
    const { x, y } = this.config.JackpotContainer.heartSymbolContainer;
    PixiBase.setPosition(heartSymbolContainer, { x: x, y: y });
    this.jackpotSceneAmountBoardContainers.addChild(heartSymbolContainer);
  }

  setupClubsSymbol() {
    const clubsSymbolContainer = createJackpotAmountSymbols(this.spriteSheet.clubsWInBoard);
    const { x, y } = this.config.JackpotContainer.clubsSymbolContainer;
    PixiBase.setPosition(clubsSymbolContainer, { x: x, y: y });
    this.jackpotSceneAmountBoardContainers.addChild(clubsSymbolContainer);
  }

  setupSpadesSymbol() {
    const spadesSymbolContainer = createJackpotAmountSymbols(this.spriteSheet.spadesWInBoard);
    const { x, y } = this.config.JackpotContainer.spadesSymbolContainer;
    PixiBase.setPosition(spadesSymbolContainer, { x: x, y: y });
    this.jackpotSceneAmountBoardContainers.addChild(spadesSymbolContainer);
  }

  setupDiamondSymbol() {
    const diamondSymbolContainer = createJackpotAmountSymbols(this.spriteSheet.diamondWInBoard);
    const { x, y } = this.config.JackpotContainer.diamondSymbolContainer;
    PixiBase.setPosition(diamondSymbolContainer, { x: x, y: y });
    this.jackpotSceneAmountBoardContainers.addChild(diamondSymbolContainer);
  }

  setupExpiresCount() {
    this.expiresCountContainer = PixiBase.createContainer('expiresCountContainer');
    this.dateText = PixiBase.createText('5:00', {
      fontSize: isMobile() ? 13 : 16,
      fill: '#FFFFFF',
      fontWeight: 400,
      resolution: 2,
      fontFamily: 'Roboto',
    });
    this.dateText.x = isMobile() ? 63 : 71;
    this.expiresText = PixiBase.createText(gameStore.canvasTranslationKeys.EXPIRY, {
      fontSize: isMobile() ? 13 : 16,
      fill: '#FFFFFF',
      fontWeight: 400,
      resolution: 2,
      fontFamily: 'Roboto',
    });
    const { x, y } = this.config.JackpotContainer.expiresDateContainerConfig;
    PixiBase.setPosition(this.expiresCountContainer, { x: x, y: y });
    this.expiresCountContainer.addChild(this.dateText);
    this.expiresCountContainer.addChild(this.expiresText);
    this.jackpotSceneAmountBoardContainers.addChild(this.expiresCountContainer);
  }

  setupJackpotWinPopupAnimation() {
    this.jackpotWinPopupAnimation = new Spine({ key: 'jackpotWinPopupAnimation' });
    this.jackpotWinPopupAnimation.visible = false;
    const { x, y } = this.config.JackpotContainer.jackpotWinPopupAnimation;
    PixiBase.setPosition(this.jackpotWinPopupAnimation, { x: x, y: y });
    this.totalAmount = new BitmapText({
      fontName: 'numbersJackpot',
      text: '',
    });
    this.totalAmount.x = 4;
    this.totalAmount.fontSize = isMobile() ? 50 : 80;
    this.countup = new CountUp(this.totalAmount);
    this.totalAmount.anchor.set(0.5);
    this.jackpotWinPopupAnimation.addChildToSlot('amount', this.totalAmount);
    this.jackpotTextureContainer.addChild(this.jackpotWinPopupAnimation);
  }

  startJackpotWinPopupAnimation(winAmount, card) {
    soundService.stop(SoundTypes.JackpotBackground);
    soundService.play(SoundTypes.JackpotWinPopUp);
    this.jackpotTextureContainer.visible = true;
    this.jackpotWinPopupAnimation.visible = true;
    this.totalAmount.text = winAmount;
    gameMainDrawer.betBar.updateLastWinAmount(`${winAmount}`);
    this.jackpotWinPopupAnimation.clearListeners();
    this.jackpotWinPopupAnimation.play('start', false);
    soundService.play(SoundTypes.WinAccumulationSound);
    this.countup.start({
      min: 0,
      max: this.totalAmount.text,
      duration: 3000,
    });
    this.jackpotWinPopupAnimation.skeleton.setSkinByName(jackpotIcons[card]);
    this.jackpotWinPopupAnimation.addListener(
      'complete',
      () => {
        this.jackpotWinPopupAnimation.play('loop', true);
      },
      this,
      'start'
    );
    this.closeTimeJackpotScane = setTimeout(() => {
      this.jackpotWinPopupAnimation.play('end', false);
      this.jackpotWinPopupAnimation.addListener(
        'whiteStart',
        () => {
          this.closeJackpot();
        },
        this,
        'end'
      );
    }, 6000);
  }

  jackpotMoveAnimation(sprite, x, y, count, jackpotCard, jackpotSceneItemInfos) {
    soundService.play(SoundTypes.JackpotSuitTransfer);
    gsap.to(sprite, {
      x: x,
      y: y,
      duration: 0.8,
      // ease: 'power2.in',
      onComplete: () => {
        sprite.play('win');
        if (count === 3) {
          this.jackpotSceneTextureContainers.children.forEach((el) => {
            if (el.key === 'jackpotSymbolContainer') {
              if (el.getCurrentAnimationName() !== 'open') {
                el.play('open');
              }
            }
          });
          jackpotSceneItemInfos.forEach((el) => {
            this.jackpotSceneTextureContainers.children[0].children[el?.index - 1].visible = true;
            this.jackpotSceneTextureContainers.children[0].children[
              el?.index - 1
            ].skeleton.setSkinByName(jackpotIcons[el?.jackpotCard]);

            this.jackpotSceneTextureContainers.children[0].children[el?.index - 1].play(
              'win',
              false
            );
          });
          this.jackpotWinPopupEndTime = setTimeout(() => {
            this.startJackpotWinPopupAnimation(
              winBarStore.jackpotSceneItemResult.jackpotSceneItemInfo?.winAmount,
              jackpotCard
            );
          }, 1000);
        }
      },
    });
    gsap.to(sprite.scale, {
      x: 1,
      y: 1,
      duration: SPIN_BUTTON_ARROWS_LOOP_ANIMATION_DURATION,
      onComplete: () => {
        gsap.to(sprite.scale, {
          x: isMobile() ? (count === 3 ? 0.6 : 0.45) : count === 3 ? 0.76 : 0.56,
          y: isMobile() ? (count === 3 ? 0.6 : 0.45) : count === 3 ? 0.76 : 0.56,
          duration: SPIN_BUTTON_ARROWS_LOOP_ANIMATION_DURATION,
        });
      },
    });
  }

  closeJackpot() {
    const distanceSymbolsPosition =
      this.config.JackpotContainer.jackpotSymbolsContainerPositionList;
    gameStore.setBonusType(null);
    winBarStore.setJackpotSceneItemResult(null);
    this.jackpotWinPopupAnimation.visible = false;
    this.jackptGameSceneBoard.interactive = false;
    this.jackpotWinPopupAnimation.visible = false;
    clearTimeout(this.closeTimeJackpotScane);
    clearTimeout(this.jackpotPopupEndTime);
    clearTimeout(this.jackpotWinPopupEndTime);
    this.jackpotSceneTextureContainer.visible = false;
    this.dateText.text = '05:00';
    gameStore.setJackpotAmountVisible(false);
    winBarStore.setJackpotSceneResult(null);
    applicationStore.setIsShowJackpot(true);
    spinBarStore.setIsSpinStart(false);
    this.jackpotSceneTextureContainers.children[0].children.forEach((el) => {
      el.visible = false;
    });
    this.jackpotSceneTextureContainers.children.forEach((el) => {
      if (el.key === 'jackpotSymbolContainer') {
        el.stop();
        el.interactive = true;
        el.buttonMode = true;
      }
    });
    this.jackpotSceneTextureContainers.children[
      this.jackpotSceneTextureContainers.children.length - 1
    ].children.forEach((el, i) => {
      el.visible = false;
      el.x = distanceSymbolsPosition[i].x;
      el.y = distanceSymbolsPosition[i].y;
      el.scale.set(1);
    });
    jackpotIconsAnimationPosition.forEach((el) => {
      el.count = 0;
    });
    soundService.stopAll();
    soundService.playMusic();
  }

  execute(config, spriteSheet) {
    const [gameImageSpriteSheet] = spriteSheet;
    this.config = config;
    this.spriteSheet = gameImageSpriteSheet;
    this.setupActiveColumn();
    this.setupJackpotGameSceneBg();
    this.setupJackpotStartPopup();
    this.setupDimondAmountBoard();
    this.setupSpadesAmountBoard();
    this.setupHeartAmountBoard();
    this.setupClubsAmountBoard();
    this.setupClubsSymbol();
    this.setupHeartSymbol();
    this.setupSpadesSymbol();
    this.setupDiamondSymbol();
    this.setupJackpotSymbolsBg();
    this.setupJackpotSymbolsContainer();
    this.setupJackpotWinPopupAnimation();
    this.setupExpiresCount();
  }
}
